/**
 * ClientsTab — clients comptables (dossiers maîtres) : n° de client (édition inline, attribution automatique des manquants),
 * membres, totaux ventes / encaissé / solde à recevoir (comptabilité du cabinet), raccourcis journal du mois & âge des comptes.
 * Monté dans AcctSubPage (portée cabinet, tuile « clients ») — la fiche client du cabinet reste le dossier Master.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, BookText, Check, Clock, ExternalLink, Hash, Pencil, Search, Users, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Empty, Field, Modal, Pill, errMsg, inputCls, money } from "./_shared";

function NumberCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.number || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/masters/${row.id}`, { client_number: val.trim() }); toast.success(val.trim() ? `N° ${val.trim().toUpperCase()} attribué` : "N° de client retiré"); setEditing(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  if (!row.is_master) return <span className="text-slate-300" title="Client sans dossier maître">—</span>;
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input value={val} onChange={(e) => setVal(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} autoFocus maxLength={20}
          className="w-24 border border-emerald-400 rounded px-1.5 py-0.5 text-xs font-mono" data-testid={`client-number-input-${row.id}`} />
        <button onClick={save} disabled={busy} className="text-emerald-700 p-0.5" data-testid={`client-number-save-${row.id}`}><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => { setEditing(false); setVal(row.number || ""); }} className="text-slate-400 p-0.5"><X className="w-3.5 h-3.5" /></button>
      </span>
    );
  }
  return (
    <button onClick={() => setEditing(true)} className="group inline-flex items-center gap-1 font-mono text-xs" title="Modifier le n° de client" data-testid={`client-number-${row.id}`}>
      {row.number ? <span className="font-semibold text-emerald-800">{row.number}</span> : <span className="text-amber-700 italic">à attribuer</span>}
      <Pencil className="w-3 h-3 text-slate-300 group-hover:text-emerald-700" />
    </button>
  );
}

function nextFree(existing, prefix, start, width) {
  let n = Math.max(1, Number(start) || 1);
  const w = Math.min(8, Math.max(1, Number(width) || 1));
  for (const num of existing) {
    const tail = prefix ? (num.startsWith(prefix) ? num.slice(prefix.length) : "") : num;
    if (/^\d+$/.test(tail)) n = Math.max(n, parseInt(tail, 10) + 1);
  }
  while (existing.includes(`${prefix}${String(n).padStart(w, "0")}`)) n += 1;
  return `${prefix}${String(n).padStart(w, "0")}`;
}

function AssignDialog({ missing, existing, onClose, onDone }) {
  const [f, setF] = useState({ prefix: "", start: 1000, width: 4 });
  const [busy, setBusy] = useState(false);
  const preview = nextFree(existing, f.prefix.toUpperCase(), f.start, f.width);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/masters/assign-client-numbers", { ...f, start: Number(f.start) || 1, width: Number(f.width) || 4 }); toast.success(`${r.data.assigned} numéro(s) attribué(s) — prochain : ${r.data.next}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Attribuer les numéros de client manquants" sub={`${missing} dossier(s) sans numéro recevront un numéro séquentiel, en ordre alphabétique, à la suite des numéros déjà utilisés.`} onClose={onClose} testId="assign-numbers-dialog"
      footer={<><Btn tone="slate" onClick={onClose} testId="assign-cancel">Annuler</Btn><Btn icon={Hash} onClick={run} busy={busy} testId="assign-run">Attribuer</Btn></>}>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Préfixe" hint="Facultatif (ex. C)"><input value={f.prefix} onChange={(e) => setF({ ...f, prefix: e.target.value.toUpperCase().slice(0, 6) })} className={`${inputCls} font-mono`} data-testid="assign-prefix" /></Field>
        <Field label="Départ"><input type="number" min="1" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={`${inputCls} font-mono`} data-testid="assign-start" /></Field>
        <Field label="Chiffres"><input type="number" min="1" max="8" value={f.width} onChange={(e) => setF({ ...f, width: e.target.value })} className={`${inputCls} font-mono`} data-testid="assign-width" /></Field>
      </div>
      <p className="text-xs text-slate-500">Premier numéro qui sera attribué : <strong className="font-mono text-slate-800" data-testid="assign-preview">{preview}</strong></p>
    </Modal>
  );
}

export default function ClientsTab({ pathFor }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [assign, setAssign] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get("/gl/customers"); setData(r.data); }
    catch (e) { toast.error(errMsg(e, "Chargement des clients impossible")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const nq = q.trim().toLowerCase();
  const items = (data?.items || []).filter((r) => (!nq || `${r.number} ${r.name} ${(r.members || []).join(" ")}`.toLowerCase().includes(nq)) && (!r.archived || r.totals.nb));
  const year = new Date().getFullYear();
  const report = (id, party) => `${pathFor("rapports")}?report=${id}${party ? `&party=${party}` : ""}`;

  return (
    <div className="space-y-4" data-testid="clients-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N°, dossier ou membre…" className={`${inputCls} pl-9 w-64`} data-testid="clients-search" />
          </div>
          {data && <span className="text-xs text-slate-500">{items.length} client(s) · à recevoir <strong className={data.total_balance > 0 ? "text-sky-700" : "text-slate-700"} data-testid="clients-total-balance">{money(data.total_balance)}</strong></span>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Btn tone="slate" icon={Clock} onClick={() => navigate(report("customer-aging"))} testId="clients-aging-link">Âge des comptes</Btn>
          <Btn tone="slate" icon={BookText} onClick={() => navigate(report("customer-journal"))} testId="clients-journal-link">Journal du mois</Btn>
          <Btn icon={Hash} onClick={() => setAssign(true)} disabled={!data?.missing_numbers} testId="clients-assign-btn">Attribuer les n° manquants</Btn>
        </div>
      </div>

      {data?.missing_numbers > 0 && (
        <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="clients-missing-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span><strong>{data.missing_numbers} dossier(s)</strong> n'ont pas encore de numéro de client. Clique sur un numéro pour le saisir à la main, ou attribue-les tous d'un coup. La fiche client comptable est le <strong>dossier maître</strong> (tuile Master) : le numéro y est aussi modifiable.</span>
        </div>
      )}

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-32">N° client</th>
              <th className="text-left px-3 py-2">Dossier</th>
              <th className="text-left px-3 py-2 hidden lg:table-cell">Membres</th>
              <th className="text-right px-3 py-2 w-20">Ventes</th>
              <th className="text-right px-3 py-2 w-32">Ventes {year}</th>
              <th className="text-right px-3 py-2 w-32">Encaissé</th>
              <th className="text-right px-3 py-2 w-32">Solde</th>
              <th className="px-2 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((r) => (
              <tr key={r.id} className={`hover:bg-emerald-50/40 ${r.archived ? "text-slate-400" : ""}`} data-testid={`client-row-${r.id}`}>
                <td className="px-3 py-2"><NumberCell row={r} onSaved={load} /></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{r.name}</span>{r.archived && <Pill>Archivé</Pill>}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 hidden lg:table-cell"><span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{(r.members || []).join(" + ") || "—"}</span></td>
                <td className="px-3 py-2 text-right tabular-nums">{r.totals.nb}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{money(r.totals.ytd)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{money(r.totals.paid)}</td>
                <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.totals.balance > 0 ? "text-sky-700 font-semibold" : ""}`} data-testid={`client-balance-${r.id}`}>{r.totals.balance ? money(r.totals.balance) : "—"}</td>
                <td className="px-2 py-2 text-right whitespace-nowrap">
                  <button onClick={() => navigate(report("customer-journal", r.id))} className="text-slate-400 hover:text-emerald-700 p-1" title="Journal du client" data-testid={`client-journal-${r.id}`}><BookText className="w-3.5 h-3.5" /></button>
                  {r.is_master && <button onClick={() => navigate(`/admin/masters/${r.id}`)} className="text-slate-400 hover:text-indigo-700 p-1" title="Ouvrir le dossier maître" data-testid={`client-open-${r.id}`}><ExternalLink className="w-3.5 h-3.5" /></button>}
                </td>
              </tr>
            ))}
            {data && !items.length && <tr><td colSpan={8}><Empty icon={Users} text="Aucun client." testId="clients-empty" /></td></tr>}
          </tbody>
        </table>
      </div>

      {assign && <AssignDialog missing={data?.missing_numbers || 0} existing={(data?.items || []).map((r) => r.number).filter(Boolean)} onClose={() => setAssign(false)} onDone={() => { setAssign(false); load(); }} />}
    </div>
  );
}
